import axios from 'axios';
import { OctagonAlert, SquareCheck, TriangleAlert } from 'lucide-react';
import React, { useState } from 'react';
import { v4 as iduuid } from 'uuid';

import { IMessage } from '@/shared/api/types/chat-message';
import { env } from '@/shared/lib';

import { PLAN_STYLES } from '../../consts';
import Button from '../../ui/button';

type Props = {
  msg: IMessage;
  ws: React.RefObject<WebSocket | null>;
  setMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  isLast: boolean;
  onReply: (msg: IMessage) => void;
  token?: string;
  isMock?: boolean;
};

type JiraActionResultPayload = {
  text: string;
  issue_key?: string;
  link?: string;
};

const normalizeJiraActionResult = (
  method: 'POST' | 'PUT' | 'DELETE',
  response: any,
): JiraActionResultPayload => {
  const { message, data } = response.data;

  switch (method) {
    case 'POST':
    case 'PUT':
      return {
        text: message,
        issue_key: data.key,
        link: data.issue_url ?? data.self,
      };

    case 'DELETE':
      return {
        text: message,
        issue_key: data.issue_key,
      };

    default:
      return {
        text: message ?? 'Операция выполнена',
      };
  }
};

const JiraPreviewMessage = ({ msg, ws, setMessages, isLast, onReply, token, isMock }: Props) => {
  const { payload } = msg;
  const [loading, setLoading] = useState(false);

  const renderPreviewContent = () => {
    const preview = payload.preview;

    switch (payload.method) {
      case 'POST':
        return (
          <>
            <p className="m-0 font-bold text-[16px]">{preview.Проект}</p>

            <div>
              <p className="font-bold">{preview.Название}</p>
              <p className="text-sm text-gray-700">{preview.Описание}</p>
            </div>

            <div className="flex gap-2 items-center">
              {preview.Приоритет && <PriorityMark mark={preview.Приоритет} />}
              {preview.Тип && (
                <div className="py-1 px-3 bg-blue-600 rounded text-white text-xs">
                  {preview.Тип}
                </div>
              )}
            </div>

            {preview.Исполнитель && (
              <p className="text-sm">
                <strong>Исполнитель:</strong> {preview.Исполнитель}
              </p>
            )}
          </>
        );

      case 'PUT':
        return (
          <>
            <p className="font-bold text-[16px]">Задача {preview.Задача}</p>

            <div className="space-y-1">
              {Object.entries(preview.Изменения ?? {}).map(([key, value]) => (
                <div key={key} className="text-sm">
                  <strong>{key}:</strong> <span className="text-gray-700">{String(value)}</span>
                </div>
              ))}
            </div>
          </>
        );

      case 'DELETE':
        return (
          <>
            <p className="font-bold text-[16px] text-red-600">Задача {preview.Задача}</p>

            <div className="flex items-center gap-2 text-red-700 font-medium">
              <OctagonAlert />
              <span>{preview.Действие}</span>
            </div>
          </>
        );

      default:
        return (
          <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(preview, null, 2)}</pre>
        );
    }
  };

  const handleConfirm = async () => {
    setLoading(true);

    // MOCK MODE
    if (isMock) {
      ws.current?.send(
        JSON.stringify({
          text: 'Да',
          reply_to: msg.id,
          reply_to_type: 'jira_preview',
        }),
      );
      return;
    }

    // PROD MODE
    try {
      const response = await axios(
        `${env.VITE_API_URL.slice(0, env.VITE_API_URL.length - 1)}${payload.endpoint}`,
        {
          method: payload.method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          data: {
            payload: {
              method: payload.method,
              data: payload.data,
            },
          },
        },
      );

      const resultPayload = normalizeJiraActionResult(payload.method, response);

      setMessages((prev) => [
        ...prev,
        {
          id: iduuid(),
          sender: 'assistant',
          type: 'jira_action_result',
          payload: resultPayload,
          createdAt: Date.now(),
        },
      ]);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        {
          id: iduuid(),
          sender: 'assistant',
          type: 'jira_response',
          payload: {
            text: e?.response?.data?.message ?? 'Ошибка при выполнении операции',
          },
          createdAt: Date.now(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: iduuid(),
        sender: 'user',
        type: 'user',
        payload: { text: '✕ Отменено' },
        createdAt: Date.now(),
      },
    ]);
  };

  const isDisabled = !isLast || loading;

  const PriorityMark = ({ mark }: { mark: 'Low' | 'Medium' | 'High' }) => {
    switch (mark) {
      case 'High':
        return (
          <div className="text-red-600 flex items-center justify-center p-[2px] h-[28px] w-[28px]">
            <OctagonAlert />
          </div>
        );
      case 'Medium':
        return (
          <div className="text-orange-500 flex items-center justify-center p-[2px] h-[28px] w-[28px]">
            <TriangleAlert />
          </div>
        );
      case 'Low':
        return (
          <div className="text-green-600 flex items-center justify-center p-[2px] h-[28px] w-[28px]">
            <SquareCheck />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex justify-end mb-6">
      <div className="bg-[#F7F7F7] rounded-lg px-4 py-3 max-w-[380px]">
        <div className="font-medium mb-2 text-black">{payload.message}</div>

        <div className="text-sm text-gray-800 bg-[#F4F5F7] border border-gray-300 rounded-2xl p-3 space-y-2">
          {renderPreviewContent()}
        </div>

        <div className="flex justify-between gap-4 max-h-[32px] items-center mt-[12px]">
          <Button
            className="text-sm !text-gray-600 cursor-pointer !bg-transparent"
            onClick={() => onReply(msg)}
          >
            ↩ ответить
          </Button>
          <div className="flex gap-2 items-center">
            <Button
              className={`${PLAN_STYLES.plan.button} text-white px-3 py-1 rounded text-sm`}
              onClick={handleConfirm}
              disabled={isDisabled}
            >
              Применить
            </Button>
            <Button
              className={`${PLAN_STYLES.decline.button} text-white px-3 py-1 rounded text-sm`}
              onClick={handleCancel}
              disabled={isDisabled}
            >
              Отменить
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraPreviewMessage;
